import { useEffect, useState } from 'react'

import { reached, saidOf, useBeat } from '~/components/use-work'
import { Working } from '~/components/working'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { useToast } from '~/components/toast-context'
import { useAuth } from '~/auth/auth-context'
import { todayKey, type WeighIn } from '~/data/weighins'
import { useApi } from '~/data/data-context'
import { coachedTargets, expenditureOf } from '~/data/expenditure'
import {
  DEFAULT_PROGRAM,
  currentGoalOn,
  formatCalories,
  isEvenWeek,
  type NutritionData,
  type Program,
} from '~/data/nutrition'
import { MacroPlate } from '~/components/macro-plate'
import { keepGoal, keepWeeklyCheckinOn, reloadNutrition } from '~/data/nutrition-store'

/** How long the number turns over before it settles, and how far either side of nothing it reaches. */
const COUNT_FOR = 1800
const COUNT_SPREAD = 400

/**
 * The weekly check-in, per the spec: the whole screen, opening on the recalculation itself —
 * a number counting in the middle until it settles on what the daily calories have moved by —
 * and then, behind it, what those calories now come to, the macros they are made of, and the
 * answers.
 *
 * Raised by the root layout on the check-in day and after it, on every opening until it is
 * answered. Leaving without answering asks first, and going on from there keeps the targets
 * in force for another week.
 */
export function WeeklyCheckinScreen({
  data,
  weighIns,
  onClose,
}: {
  data: NutritionData
  weighIns: readonly WeighIn[]
  onClose: () => void
}) {
  const api = useApi()
  const toast = useToast()
  const { settings } = useAuth()
  const [busy, setBusy] = useState(false)
  /** Which answer is being sent — the one that says it is working; the other waits dimmed. */
  const [taking, setTaking] = useState<boolean | null>(null)
  const busyShown = useBeat(busy)
  const [leaving, setLeaving] = useState(false)
  const today = todayKey()
  // The goal in force with what it is asking of today, which is what the new numbers are
  // weighed against.
  const goal = currentGoalOn(data.goals, today)
  const aim = goal?.aim ?? null
  const spent = expenditureOf(data, weighIns, settings, today)
  const expenditure = spent.value
  /*
   * The check-in arrives at the targets the goal itself did: the same program, and — where the
   * goal maintains — the same target trend weight, which is what makes maintenance dynamic.
   */
  const program: Program =
    goal === null
      ? DEFAULT_PROGRAM
      : {
          diet: goal.diet ?? DEFAULT_PROGRAM.diet,
          calorieFloor: goal.calorieFloor ?? DEFAULT_PROGRAM.calorieFloor,
          proteinIntake: goal.proteinIntake ?? DEFAULT_PROGRAM.proteinIntake,
          distribution: goal.calorieDistribution ?? DEFAULT_PROGRAM.distribution,
        }
  const got =
    aim === null
      ? null
      : coachedTargets(
          spent,
          settings,
          aim,
          goal?.rateKgPerWeek ?? null,
          program,
          aim === 'maintain' ? (goal?.targetWeightKg ?? null) : null,
        )
  const implied = got !== null && 'targets' in got ? got.targets : null

  const change =
    implied === null || goal === null ? 0 : Math.round(implied.calories - goal.calories)
  /*
   * A change of nothing, and a week nothing can be worked out for, come to the same screen:
   * there is no new set of targets to weigh against the old, so there is nothing to choose
   * between and one way on rather than two.
   */
  const nothingToChoose = implied === null || expenditure === null || change === 0
  const shown = nothingToChoose ? goal : implied
  const settled = useCounted(change)

  /**
   * The answer, sent. Refused where the server was not reached — see [reached] — which the
   * confirmation to leave says in itself, and a press on the page says as a toast.
   */
  const answer = async (take: boolean) => {
    if (goal === null || busy) {
      return
    }
    setBusy(true)
    setTaking(take)
    try {
      if (take && implied !== null) {
        /*
         * New targets, added to the goal in force from today on — and no goal set. The
         * check-in asked about what a day is given and about nothing the goal aims at, so the
         * goal is the goal it was: the same day it began on, the same stretch, the same place
         * among the goals gone by. Setting one here is what used to leave somebody with a
         * goal a week and a dial that restarted every Monday.
         */
        const saved = await reached(() =>
          api.addGoalTargets({
            effectiveFrom: today,
            calories: implied.calories,
            protein: implied.protein,
            carbs: implied.carbs,
            fat: implied.fat,
            expenditureAtSet: expenditure,
            dayTargets: isEvenWeek(implied.days) ? null : implied.days,
          }),
        )
        keepGoal(saved)
      }
      await reached(() => api.weeklyCheckedIn(today))
    } finally {
      setBusy(false)
    }
    keepWeeklyCheckinOn(today)
    reloadNutrition()
    toast(take ? 'New targets set from today.' : 'Targets kept for another week.')
    onClose()
  }

  /** An answer pressed on the page, whose failure is said as a toast. */
  const answerHere = (take: boolean) => {
    answer(take).catch((error: unknown) => {
      toast(saidOf(error))
    })
  }

  if (goal === null || shown === null) {
    return null
  }

  const sign = settled === null ? '' : settled > 0 ? '+' : settled < 0 ? '−' : '±'
  const figure = settled === null ? '' : String(Math.abs(settled))

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Weekly check-in"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 overflow-y-auto bg-page px-6 py-10"
    >
      {/* The heading says nothing until the counting has stopped: over a figure still moving it
          would be labelling something that is not yet an answer. */}
      <h1
        className={`text-center text-lg text-ink-muted transition-opacity duration-300 ${
          settled === null ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {nothingToChoose ? 'Your daily calories hold' : 'Your new daily calories'}
      </h1>

      <CountingChange sign={sign} figure={figure} settled={settled !== null} />

      <div
        className={`flex w-full max-w-sm flex-col items-center gap-5 transition-opacity duration-300 ${
          settled === null ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <p className="text-3xl font-semibold tabular-nums">
          {formatCalories(shown.calories)} a day
        </p>
        <MacroPlate
          protein={shown.protein}
          carbs={shown.carbs}
          fat={shown.fat}
          caloriesShown={false}
        />
        {nothingToChoose && (
          <p className="text-center text-sm text-ink-muted">
            {expenditure === null
              ? 'There is not enough logged yet to work out what you burn, so the targets stay as they are.'
              : 'What you have logged this week puts you where the goal expects you to be, so the targets stay as they are.'}
          </p>
        )}
        <div className="flex w-full flex-col gap-2">
          {nothingToChoose ? (
            <button
              type="button"
              aria-busy={busy || undefined}
              disabled={!busy && settled === null}
              className="relative w-full rounded-full bg-accent px-5 py-3 text-sm font-medium text-accent-ink disabled:opacity-50"
              onClick={() => {
                answerHere(false)
              }}
            >
              <Working shown={busyShown}>Carry on</Working>
            </button>
          ) : (
            <>
              <button
                type="button"
                aria-busy={(busy && taking === true) || undefined}
                disabled={busy && taking !== true}
                className="relative w-full rounded-full bg-accent px-5 py-3 text-sm font-medium text-accent-ink disabled:opacity-50"
                onClick={() => {
                  answerHere(true)
                }}
              >
                <Working shown={busyShown && taking === true}>Take the new targets</Working>
              </button>
              <button
                type="button"
                aria-busy={(busy && taking === false) || undefined}
                disabled={busy && taking !== false}
                className="relative w-full rounded-full px-5 py-3 text-sm text-ink-muted transition-colors hover:bg-hover hover:text-ink disabled:opacity-50"
                onClick={() => {
                  answerHere(false)
                }}
              >
                <Working shown={busyShown && taking === false}>
                  Keep last week&rsquo;s for another week
                </Working>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Leaving without answering is not a way of putting the check-in off — it comes back on
          the next opening either way — so it is asked about first. */}
      <button
        type="button"
        aria-label="Close the weekly check-in"
        disabled={busy}
        className="absolute top-4 right-4 rounded-full px-3 py-2 text-sm text-ink-muted transition-colors hover:bg-hover hover:text-ink"
        onClick={() => {
          setLeaving(true)
        }}
      >
        Close
      </button>
      {leaving && (
        <ConfirmDialog
          title="Keep last week's targets?"
          body="Leaving the check-in keeps the targets you are on for another week."
          confirm="Keep them"
          onConfirm={async () => {
            await answer(false)
            setLeaving(false)
          }}
          onDismiss={() => {
            setLeaving(false)
          }}
        />
      )}
    </div>
  )
}

/**
 * The recalculation, drawn as it happens: digits turning over in the middle of the screen,
 * slowing as they go, until they stop on what the daily calories have moved by. Returns null
 * while it is still turning and the settled figure once it is not.
 *
 * Where the browser has been asked to do without motion it settles at once — a screen whose
 * only content stands behind a flourish is a screen somebody who turned flourishes off cannot
 * read.
 */
function useCounted(to: number): number | null {
  /*
   * Decided once, as the screen opens: whether to roll at all. Guarded by a typeof rather than
   * by optional chaining, because not every environment the app renders in has matchMedia and
   * the types say otherwise.
   */
  const [rolling, setRolling] = useState(
    () =>
      typeof window.matchMedia === 'function' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    if (!rolling) {
      return
    }
    const timer = window.setTimeout(() => {
      setRolling(false)
    }, COUNT_FOR)
    return () => {
      window.clearTimeout(timer)
    }
  }, [rolling])

  return rolling ? null : to
}

/** The turning digits themselves, which say nothing to a screen reader until they stop. */
function CountingChange({
  sign,
  figure,
  settled,
}: {
  sign: string
  figure: string
  settled: boolean
}) {
  const [rolling, setRolling] = useState(0)

  useEffect(() => {
    if (settled) {
      return
    }
    const timer = window.setInterval(() => {
      setRolling(Math.round((Math.random() * 2 - 1) * COUNT_SPREAD))
    }, 60)
    return () => {
      window.clearInterval(timer)
    }
  }, [settled])

  return (
    <p
      aria-live="polite"
      aria-label={settled ? `Daily calories move by ${sign}${figure}` : 'Working out this week'}
      className={`text-7xl font-bold tabular-nums transition-colors ${
        settled ? 'text-accent' : 'text-ink-muted'
      }`}
    >
      {settled ? `${sign}${figure}` : `${rolling > 0 ? '+' : '−'}${String(Math.abs(rolling))}`}
    </p>
  )
}
